import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icon } from '../icons';
import Select from './Select';
import DateTimePicker from './DateTimePicker';
import SearchInput from './SearchInput';
import Pagination, { usePagination, useFitPageSize } from './Pagination';
import HelpTip from './HelpTip';
import KpiGrid from './KpiGrid';
import Drawer from './Drawer';
import { useConfirm } from './ConfirmProvider';
import { useAsyncAction } from '../hooks/useAsyncAction';

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB

// --- Audit ("Canale aggiornamenti") ---------------------------------------
// Mappe di presentazione per categorie/azioni/esiti del registro di audit.
const AUDIT_CATEGORIES = {
  auth: { label: 'Autenticazione', tone: 'brand' },
  users: { label: 'Utenti', tone: 'brand' },
  licenses: { label: 'Licenze', tone: 'accent' },
  activation_codes: { label: 'Codici', tone: 'accent' },
  reactivation: { label: 'Riattivazioni', tone: 'warn' },
  updates: { label: 'Aggiornamenti', tone: 'brand' },
  support: { label: 'Supporto', tone: 'warn' },
  system: { label: 'Sistema', tone: 'brand' },
};
const AUDIT_ACTION_LABELS = {
  login_success: 'Login', login_failed: 'Login fallito', login_blocked: 'Login bloccato',
  login_denied_disabled: 'Login negato (disabilitato)', logout: 'Logout',
  change_password: 'Cambio password', change_password_failed: 'Cambio password fallito',
  password_reset_requested: 'Recupero password richiesto', password_reset_completed: 'Password reimpostata (recupero)',
  activation_code_created: 'Codice creato', activation_code_revoked: 'Codice revocato',
  license_status_changed: 'Licenza: stato cambiato', license_expiry_set: 'Licenza: scadenza modificata',
  license_marked_ready: 'Licenza: pronta per attivazione', license_reverted: 'Licenza: attivazione annullata', license_cancelled: 'Licenza: setup annullato',
  reactivation_request_approved: 'Riattivazione approvata', reactivation_request_rejected: 'Riattivazione rifiutata',
  user_created: 'Utente creato', user_role_changed: 'Ruolo cambiato', user_email_changed: 'Email aggiornata',
  user_enabled: 'Utente riabilitato', user_disabled: 'Utente disabilitato',
  user_password_reset: 'Reset password', user_deleted_soft: 'Utente disabilitato', user_deleted_hard: 'Utente eliminato',
  upload_published: 'Versione pubblicata', upload_file_deleted: 'File eliminato', upload_version_deleted: 'Versione eliminata',
  release_approved: 'Versione approvata', release_test_downloaded: 'Installer di collaudo scaricato',
  release_revoked: 'Versione revocata', uploads_bulk_deleted: 'Versioni archiviate svuotate',
  device_deleted: 'Dispositivo eliminato', activation_code_deleted: 'Codice eliminato',
  audit_purged: 'Registro eventi svuotato',
  support_report_created: 'Segnalazione aperta',
  support_report_closed: 'Segnalazione gestita', support_report_reopened: 'Segnalazione riaperta',
  register: 'Registrazione', activate: 'Attivazione', validate: 'Validazione licenza', 'check-update': 'Controllo aggiornamento', download: 'Download',
};
const AUDIT_OUTCOMES = {
  success: { label: 'ok', cls: 'badge-success' },
  failure: { label: 'errore', cls: 'badge-danger' },
  denied: { label: 'negato', cls: 'badge-warn' },
};
const AUDIT_CATEGORY_OPTIONS = [
  { value: '', label: 'Tutte le categorie' },
  { value: 'auth', label: 'Autenticazione' },
  { value: 'users', label: 'Utenti' },
  { value: 'licenses', label: 'Licenze' },
  { value: 'activation_codes', label: 'Codici' },
  { value: 'reactivation', label: 'Riattivazioni' },
  { value: 'updates', label: 'Aggiornamenti' },
  { value: 'support', label: 'Supporto' },
];
const AUDIT_SOURCE_OPTIONS = [
  { value: '', label: 'Tutte le origini' },
  { value: 'operator', label: 'Operatori' },
  { value: 'machine', label: 'Macchine' },
  { value: 'system', label: 'Sistema' },
];
const AUDIT_OUTCOME_OPTIONS = [
  { value: '', label: 'Tutti gli esiti' },
  { value: 'success', label: 'Successo' },
  { value: 'failure', label: 'Errore' },
  { value: 'denied', label: 'Negato' },
];
const auditActionLabel = (a) => AUDIT_ACTION_LABELS[a] || String(a || '').replace(/[_-]/g, ' ');

// Come mostrare l'attore in tabella: operatore (username/ruolo), macchina
// (machine_id/license_key) oppure evento interno di sistema.
function auditActor(ev) {
  if (ev.actor_username) return { main: ev.actor_username, sub: ev.actor_role || (ev.source === 'operator' ? '' : ev.source) };
  if (ev.actor_machine_id) return { main: ev.actor_machine_id, sub: 'macchina' };
  if (ev.actor_license_key) return { main: ev.actor_license_key, sub: 'licenza' };
  return { main: 'Sistema', sub: '' };
}

// Riga di dettaglio breve derivata dai metadata (una riga leggibile).
function auditShortDetail(ev) {
  const m = (ev.metadata && typeof ev.metadata === 'object') ? ev.metadata : {};
  if (m.previous_status && m.new_status) return `${m.previous_status} → ${m.new_status}`;
  if (m.previous_role && m.new_role) return `${m.previous_role} → ${m.new_role}`;
  if (m.version) return `v${m.version}`;
  if (m.files_count != null) return `${m.files_count} file`;
  if (m.mode) return m.mode === 'auto' ? 'password generata' : 'password manuale';
  if (m.reason) return String(m.reason).replace(/_/g, ' ');
  if (m.detail) return m.detail;
  if (m.area_label) return m.area_label;
  return '';
}

function formatSize(bytes) {
  const b = Number(bytes);
  if (!b) return '—';
  if (b < 1024) return b + ' B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
  if (b < 1024 * 1024 * 1024) return (b / (1024 * 1024)).toFixed(1) + ' MB';
  return (b / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}
const fmt = (d) => (d ? new Date(d).toLocaleString('it-IT') : '—');

// Confronto semantico di versioni, gemello di server/versions.js: serve a
// ordinare lo storico dalla più recente alla più vecchia (1.10.0 dopo 1.9.0, che
// un ordinamento alfabetico sbaglierebbe). Quale versione sia online lo decide il
// server, non questo confronto.
function compareVersionsJs(a, b) {
  const pa = String(a).split('.').map(Number);
  const pb = String(b).split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
}

function DashboardSviluppatore({ section, onBadges }) {
  const confirm = useConfirm();
  const [files, setFiles] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState(null); // array di File | null
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [version, setVersion] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState(null);
  const [openVersions, setOpenVersions] = useState({});
  const [fileSearch, setFileSearch] = useState('');
  // Storico versioni: quali sono state pubblicate e quale e' in distribuzione.
  const [releases, setReleases] = useState([]);

  // Audit ("Canale aggiornamenti"): sorgente unica /api/audit, filtri e
  // paginazione lato server.
  const [auditItems, setAuditItems] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditSummary, setAuditSummary] = useState(null);
  const [aq, setAq] = useState('');
  const [aqDebounced, setAqDebounced] = useState('');
  const [aCategory, setACategory] = useState('');
  const [aOutcome, setAOutcome] = useState('');
  const [aSource, setASource] = useState('');
  const [aFrom, setAFrom] = useState('');
  const [aTo, setATo] = useState('');
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(12);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditDetail, setAuditDetail] = useState(null);
  const [auditExporting, setAuditExporting] = useState(false);

  const loadFiles = useCallback(async () => {
    try {
      const res = await fetch('/api/dev/uploads', { credentials: 'include' });
      if (res.ok) setFiles(await res.json());
    } catch { /* ignore */ }
  }, []);
  const loadEvents = useCallback(async () => {
    try {
      const res = await fetch('/api/dev/update-events', { credentials: 'include' });
      if (res.ok) setEvents(await res.json());
    } catch { /* ignore */ }
  }, []);
  const loadReleases = useCallback(async () => {
    try {
      const res = await fetch('/api/dev/releases', { credentials: 'include' });
      if (res.ok) setReleases(await res.json());
    } catch { /* ignore */ }
  }, []);

  // Parametri di filtro comuni (senza pagina) per lista, summary ed export.
  const auditQueryString = useCallback(() => {
    const p = new URLSearchParams();
    if (aqDebounced) p.set('q', aqDebounced);
    if (aCategory) p.set('category', aCategory);
    if (aOutcome) p.set('outcome', aOutcome);
    if (aSource) p.set('source', aSource);
    if (aFrom) p.set('from', aFrom);
    if (aTo) p.set('to', aTo);
    return p;
  }, [aqDebounced, aCategory, aOutcome, aSource, aFrom, aTo]);

  // fetch + blob invece di un semplice <a href>: un click su un link genera una
  // navigazione con Accept: text/html, che il proxy dev di CRA non inoltra al
  // backend (serve invece l'index.html della SPA). fetch() non ha questo problema.
  const handleExportAudit = useCallback(async () => {
    setAuditExporting(true);
    try {
      const res = await fetch(`/api/audit/events/export.csv?${auditQueryString().toString()}`, { credentials: 'include' });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `registro-audit-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
    finally { setAuditExporting(false); }
  }, [auditQueryString]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const p = auditQueryString();
      p.set('page', String(auditPage));
      p.set('page_size', String(auditPageSize));
      const res = await fetch(`/api/audit/events?${p.toString()}`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setAuditItems(d.items || []); setAuditTotal(d.total || 0); }
    } catch { /* ignore */ }
    setAuditLoading(false);
  }, [auditQueryString, auditPage, auditPageSize]);

  const loadAuditSummary = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (aFrom) p.set('from', aFrom);
      if (aTo) p.set('to', aTo);
      const res = await fetch(`/api/audit/summary?${p.toString()}`, { credentials: 'include' });
      if (res.ok) setAuditSummary(await res.json());
    } catch { /* ignore */ }
  }, [aFrom, aTo]);

  useEffect(() => { loadFiles(); }, [loadFiles]);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { loadReleases(); }, [loadReleases]);
  // Debounce della ricerca (e reset a pagina 1 a ogni nuova query).
  useEffect(() => {
    const t = setTimeout(() => { setAqDebounced(aq.trim()); setAuditPage(1); }, 300);
    return () => clearTimeout(t);
  }, [aq]);
  useEffect(() => { if (section === 'eventi') loadAudit(); }, [section, loadAudit]);
  useEffect(() => { if (section === 'eventi') loadAuditSummary(); }, [section, loadAuditSummary]);

  // Scorciatoie di periodo per il range date (datetime-local locale).
  const setQuickRange = (days) => {
    const pad = (n) => String(n).padStart(2, '0');
    const toLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    const now = new Date();
    const from = days === 0
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
      : new Date(now.getTime() - days * 86400000);
    setAFrom(toLocal(from));
    setATo('');
    setAuditPage(1);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFiles || selectedFiles.length === 0) return;
    if (!version.trim()) { setMessage({ type: 'error', text: 'La versione è obbligatoria' }); return; }
    setMessage(null);
    try {
      const check = await fetch('/api/dev/validate-version', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ version: version.trim() }),
      });
      if (!check.ok) { const err = await check.json(); setMessage({ type: 'error', text: err.error || 'Versione non valida' }); return; }
    } catch { setMessage({ type: 'error', text: 'Errore di connessione' }); return; }

    setUploading(true); setProgress(0);
    const allFiles = Array.from(selectedFiles);
    const totalSize = allFiles.reduce((s, f) => s + f.size, 0);
    let uploaded = 0;
    try {
      const initRes = await fetch('/api/dev/upload-init', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ version: version.trim() }) });
      if (!initRes.ok) throw new Error((await initRes.json()).error || 'Errore init');
      const { uploadId } = await initRes.json();
      for (const file of allFiles) {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        for (let ci = 0; ci < totalChunks; ci++) {
          const start = ci * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const form = new FormData();
          form.append('chunk', file.slice(start, end));
          form.append('uploadId', uploadId);
          form.append('fileName', file.name);
          form.append('chunkIndex', ci);
          form.append('totalChunks', totalChunks);
          const cr = await fetch('/api/dev/upload-chunk', { method: 'POST', credentials: 'include', body: form });
          if (!cr.ok) throw new Error((await cr.json()).error || `Errore chunk ${ci + 1}/${totalChunks}`);
          uploaded += (end - start);
          setProgress(Math.round((uploaded / totalSize) * 100));
        }
      }
      const done = await fetch('/api/dev/upload-complete', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ uploadId }) });
      const data = await done.json();
      if (!done.ok) throw new Error(data.error || 'Errore finalizzazione');
      // Caricare non è più pubblicare: la versione nasce «Da testare» e resta
      // invisibile alle macchine finché qualcuno non la prova e la approva.
      setMessage({ type: 'success', text: data.message || `${data.files.length} file caricati: versione da testare, in attesa di approvazione` });
      setSelectedFiles(null); setVersion(''); e.target.reset();
      loadFiles(); loadEvents(); loadReleases();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Errore upload' });
    }
    setUploading(false);
  };

  // Il server rifiuta di eliminare cio' che e' in distribuzione (409): senza
  // mostrarlo, il click sembrerebbe semplicemente non fare nulla.
  const handleDelete = async (id) => {
    if (!(await confirm({
      title: 'Elimina file',
      message: 'Eliminare questo file?\n\nVerrà rimosso anche dallo storage. Azione non reversibile.',
      confirmLabel: 'Elimina file',
      tone: 'danger',
      icon: 'trash',
    }))) return;
    const res = await fetch(`/api/dev/uploads/${id}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) { loadFiles(); loadReleases(); return; }
    const err = await res.json().catch(() => ({}));
    confirm({
      title: 'Eliminazione non consentita',
      message: err.error || 'Impossibile eliminare il file.',
      confirmLabel: 'Ho capito',
      hideCancel: true,
      icon: 'alert',
    });
  };
  const { run: runDelete, pending: deletingFile } = useAsyncAction(handleDelete);

  const handleDeleteVersion = async (ver) => {
    if (!(await confirm({
      title: 'Elimina versione',
      message: `Eliminare la versione ${ver} e tutti i suoi file?\n\nVerranno rimossi anche dallo storage e la versione uscirà dallo storico. Azione non reversibile.`,
      confirmLabel: 'Elimina versione',
      tone: 'danger',
      icon: 'trash',
    }))) return;
    const res = await fetch(`/api/dev/uploads/version/${encodeURIComponent(ver)}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) { loadFiles(); loadReleases(); return; }
    const err = await res.json().catch(() => ({}));
    confirm({
      title: 'Eliminazione non consentita',
      message: err.error || 'Impossibile eliminare la versione.',
      confirmLabel: 'Ho capito',
      hideCancel: true,
      icon: 'alert',
    });
  };
  const { run: runDeleteVersion, pending: deletingVersion } = useAsyncAction(handleDeleteVersion);

  // Revoca la versione in distribuzione: sposta il puntatore alla precedente
  // (o a nessuna, se questa era l'unica mai pubblicata) — vedi POST
  // /api/dev/releases/revoke. Non elimina nulla: la versione revocata torna
  // "archiviata" e diventa eliminabile con l'azione sopra. `releases` arriva
  // dal server gia' ordinata dalla piu' recente alla piu' vecchia, quindi la
  // riga subito dopo la corrente e' la versione a cui si tornerebbe.
  const handleRevokeRelease = async () => {
    if (!currentRel) return;
    const idx = releases.findIndex((r) => r.is_current);
    const fallback = idx >= 0 && idx + 1 < releases.length ? releases[idx + 1] : null;
    if (!(await confirm({
      title: 'Revoca distribuzione',
      message: fallback
        ? `Revocare la versione ${currentRel.version}?\n\nLe macchine torneranno a scaricare la ${fallback.version}, che ridiventa quella in distribuzione. La ${currentRel.version} resta nello storico come archiviata.`
        : `Revocare la versione ${currentRel.version}?\n\nÈ l'unica versione mai pubblicata: nessuna versione resterà in distribuzione finché non ne pubblichi una nuova. Le macchine non riceveranno aggiornamenti nel frattempo.`,
      confirmLabel: 'Revoca distribuzione',
      tone: 'danger',
      icon: 'ban',
    }))) return;
    const res = await fetch('/api/dev/releases/revoke', { method: 'POST', credentials: 'include' });
    if (res.ok) { loadFiles(); loadReleases(); return; }
    const err = await res.json().catch(() => ({}));
    confirm({
      title: 'Operazione non consentita',
      message: err.error || 'Impossibile revocare la versione.',
      confirmLabel: 'Ho capito',
      hideCancel: true,
      icon: 'alert',
    });
  };
  const { run: runRevokeRelease, pending: revokingRelease } = useAsyncAction(handleRevokeRelease);

  // Approva una versione in collaudo: da quel momento è lei che le macchine
  // scaricano. L'endpoint non è sotto /api/dev perché l'approvazione spetta
  // anche a tecnico e amministratore (vedi server/routes/releases.js).
  const handleApproveRelease = async (ver) => {
    if (!(await confirm({
      title: 'Approva versione',
      message: currentRel
        ? `Approvare la versione ${ver}?\n\nDiventa l'aggiornamento distribuito: le macchine la scaricheranno al prossimo controllo, al posto della ${currentRel.version}. Approvala solo dopo averla provata.`
        : `Approvare la versione ${ver}?\n\nDiventa l'aggiornamento distribuito: le macchine la scaricheranno al prossimo controllo. Approvala solo dopo averla provata.`,
      confirmLabel: 'Approva e distribuisci',
      icon: 'check',
    }))) return;
    const res = await fetch(`/api/releases/${encodeURIComponent(ver)}/approve`, { method: 'POST', credentials: 'include' });
    if (res.ok) { loadFiles(); loadReleases(); return; }
    const err = await res.json().catch(() => ({}));
    confirm({
      title: 'Approvazione non riuscita',
      message: err.error || 'Impossibile approvare la versione.',
      confirmLabel: 'Ho capito',
      hideCancel: true,
      icon: 'alert',
    });
  };
  const { run: runApproveRelease, pending: approvingRelease } = useAsyncAction(handleApproveRelease);

  // Svuota tutto l'archivio in un colpo (mai la corrente, stessa protezione
  // server-side delle eliminazioni singole di sopra).
  const handleBulkDeleteVersions = async () => {
    // Le versioni da testare non sono archivio: restano fuori dallo svuotamento
    // (stessa regola applicata dal server).
    const archivedCount = releases.filter((r) => !r.is_current && r.status !== 'da_testare').length;
    if (archivedCount === 0) return;
    if (!(await confirm({
      title: 'Svuota versioni archiviate',
      message: `Eliminare tutte le ${archivedCount} versioni archiviate e i loro file?\n\nLa versione in distribuzione (${currentVersion}) non viene toccata. Azione non reversibile.`,
      confirmLabel: 'Svuota versioni archiviate',
      tone: 'danger',
      icon: 'trash',
    }))) return;
    const res = await fetch('/api/dev/uploads', { method: 'DELETE', credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      confirm({
        title: 'Operazione non consentita',
        message: data.error || 'Impossibile svuotare le versioni archiviate.',
        confirmLabel: 'Ho capito',
        hideCancel: true,
        icon: 'alert',
      });
      return;
    }
    loadFiles(); loadReleases();
  };
  const { run: runBulkDeleteVersions, pending: bulkDeletingVersions } = useAsyncAction(handleBulkDeleteVersions);

  // Svuota il registro eventi/audit (audit_events + update_events, le due fonti
  // che v_audit_events unisce). Vedi DELETE /api/audit/events: resta solo la
  // riga di questo stesso svuotamento.
  const handlePurgeAudit = async () => {
    if (!(await confirm({
      title: 'Svuota registro',
      message: 'Svuotare TUTTO il registro eventi/audit?\n\nSi perde ogni traccia di login, azioni amministrative ed eventi macchina registrati fin qui — resterà solo la riga di questo stesso svuotamento. Azione non reversibile.',
      confirmLabel: 'Svuota registro',
      tone: 'danger',
      icon: 'trash',
    }))) return;
    const res = await fetch('/api/audit/events', { method: 'DELETE', credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      confirm({
        title: 'Operazione non consentita',
        message: data.error || 'Impossibile svuotare il registro.',
        confirmLabel: 'Ho capito',
        hideCancel: true,
        icon: 'alert',
      });
      return;
    }
    setAuditPage(1);
    loadAudit(); loadAuditSummary();
  };
  const { run: runPurgeAudit, pending: purgingAudit } = useAsyncAction(handlePurgeAudit);

  const toggleVersion = (v) => setOpenVersions((p) => ({ ...p, [v]: !p[v] }));

  // --- Selezione file per la pubblicazione (drag-drop + picker, lista mutabile) ---
  const openFilePicker = () => { if (!uploading) fileInputRef.current?.click(); };
  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setSelectedFiles((prev) => {
      const cur = prev || [];
      const key = (f) => `${f.name}:${f.size}`;
      const seen = new Set(cur.map(key));
      return [...cur, ...incoming.filter((f) => !seen.has(key(f)))];
    });
  };
  const removeFile = (i) => setSelectedFiles((prev) => {
    const next = (prev || []).filter((_, idx) => idx !== i);
    return next.length ? next : null;
  });
  const onDropFiles = (e) => { e.preventDefault(); setDragging(false); if (!uploading) addFiles(e.dataTransfer.files); };
  const onDragOverFiles = (e) => { e.preventDefault(); if (!uploading && !dragging) setDragging(true); };
  const onDragLeaveFiles = (e) => { e.preventDefault(); setDragging(false); };

  // "Versione corrente" è quella marcata in distribuzione dal server, non la prima
  // riga dell'elenco file: ora che lo storico conserva tutto, dedurla dai file
  // sarebbe fragile.
  const currentRel = releases.find((r) => r.is_current) || null;
  const currentVersion = currentRel ? currentRel.version : '—';
  const okCount = events.filter((e) => e.status === 'ok').length;
  const errCount = events.filter((e) => e.status === 'error').length;

  const filteredFiles = useMemo(() => {
    const q = fileSearch.trim().toLowerCase();
    if (!q) return files;
    return files.filter((f) => [f.original_name, f.stored_name, f.version, f.file_type].some((v) => (v || '').toLowerCase().includes(q)));
  }, [files, fileSearch]);
  const visibleGrouped = filteredFiles.reduce((acc, f) => { const v = f.version || 'Senza versione'; (acc[v] = acc[v] || []).push(f); return acc; }, {});
  // Dalla più recente alla più vecchia. Il gruppo "Senza versione" (file orfani di
  // vecchi upload) non è confrontabile e resta in fondo.
  const versionEntries = Object.entries(visibleGrouped).sort(([a], [b]) => {
    const va = /^\d/.test(a);
    const vb = /^\d/.test(b);
    if (!va || !vb) return va === vb ? 0 : (va ? -1 : 1);
    return compareVersionsJs(b, a);
  });

  // Tre stati possibili: appena caricata e in attesa di collaudo, in
  // distribuzione (una sola per volta), o archiviata perché superata.
  const releaseByVersion = (ver) => releases.find((r) => r.version === ver) || null;
  const releaseStatus = (ver) => {
    const r = releaseByVersion(ver);
    if (r && r.status === 'da_testare') return { label: 'Da testare', cls: 'badge-warn' };
    if (currentRel && ver === currentRel.version) return { label: 'In distribuzione', cls: 'badge-success' };
    return { label: 'Archiviata', cls: 'badge-muted' };
  };

  const filePagination = usePagination(versionEntries.length, [fileSearch]);
  const pagedVersionEntries = versionEntries.slice(filePagination.start, filePagination.end);

  // Canale aggiornamenti: stessa logica fit-to-screen delle Licenze, la lista
  // non fa mai scrollare la pagina. Qui la pageSize calcolata guida il
  // page_size richiesto al server.
  const eventCardRef = useRef(null);
  const eventWrapRef = useRef(null);
  const eventTheadRef = useRef(null);
  useFitPageSize(
    { cardRef: eventCardRef, wrapRef: eventWrapRef, theadRef: eventTheadRef, bottomGap: 32 },
    auditPageSize, setAuditPageSize,
    section === 'eventi' && auditItems.length > 0
  );

  // ---- PANORAMICA ----
  if (section === 'panoramica') {
    const stats = [
      { icon: 'package', cls: 'brand', value: currentVersion, label: 'Versione in distribuzione' },
      { icon: 'upload', cls: 'accent', value: releases.length, label: 'Versioni pubblicate' },
      { icon: 'activity', cls: 'brand', value: okCount, label: 'Eventi OK' },
      { icon: 'alert', cls: 'accent', value: errCount, label: 'Eventi in errore' },
    ];
    return (
      <>
        <div className="stat-grid">
          {stats.map((s, i) => (
            <div className="stat-card" key={i}>
              <div className={`stat-icon ${s.cls}`}><Icon name={s.icon} size={20} /></div>
              <div className="stat-text">
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="card">
          <div className="card-head"><h2><Icon name="activity" size={18} />Ultimi eventi del canale</h2></div>
          {events.length === 0 ? <div className="empty"><Icon name="activity" size={30} /><span>Nessun evento</span></div> : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th style={{ width: '22%' }}>Tipo</th><th style={{ width: '20%' }}>Esito</th><th style={{ width: '26%' }}>Versione</th><th style={{ width: '32%' }}>Quando</th></tr></thead>
                <tbody>
                  {events.slice(0, 6).map((ev) => (
                    <tr key={ev.id}>
                      <td className="strong">{ev.event_type}</td>
                      <td><span className={`badge ${ev.status === 'ok' ? 'badge-success' : 'badge-danger'}`}>{ev.status}</span></td>
                      <td className="mono">{ev.version || '—'}</td>
                      <td>{fmt(ev.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>
    );
  }

  // ---- PUBBLICA ----
  if (section === 'pubblica') {
    const picked = selectedFiles || [];
    const totalSize = picked.reduce((s, f) => s + f.size, 0);
    const extOf = (name) => { const i = name.lastIndexOf('.'); return i >= 0 ? name.slice(i) : 'file'; };
    return (
      <div className="publish-grid">
        <div className="card">
          <div className="card-head card-head--stacked">
            <h2><Icon name="upload" size={18} />Pubblica aggiornamento<HelpTip text="Usala ogni volta che rilasci una nuova versione di HIPLAAS: carichi l'installer col numero di versione e la versione entra in stato «Da testare». Le macchine NON la scaricano finché non viene approvata da chi l'ha provata (tu, il tecnico o l'amministratore) da «Versioni pubblicate»." /></h2>
            <span className="card-sub">I file vengono caricati nello storage e vanno subito in distribuzione alle macchine con licenza valida. Le versioni precedenti restano archiviate.</span>
          </div>
          <form onSubmit={handleUpload}>
            <div className="field">
              <label htmlFor="ver">Numero di versione</label>
              <input id="ver" className="input" style={{ maxWidth: 320 }} placeholder="es. 1.2.0" value={version} onChange={(e) => setVersion(e.target.value)} disabled={uploading} />
              <span className="hint">Deve essere superiore alla versione più alta mai caricata: i numeri già usati non si riciclano. La versione resta «Da testare» finché non viene approvata.</span>
            </div>
            <div className="field">
              <label>File installer</label>
              <div
                className={`dropzone${dragging ? ' is-dragging' : ''}${uploading ? ' is-disabled' : ''}`}
                role="button"
                tabIndex={0}
                onClick={openFilePicker}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFilePicker(); } }}
                onDragOver={onDragOverFiles}
                onDragLeave={onDragLeaveFiles}
                onDrop={onDropFiles}
              >
                <input ref={fileInputRef} type="file" accept=".exe,.blockmap,.yml,.yaml" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
                <div className="dropzone-icon"><Icon name="upload" size={24} /></div>
                <div className="dropzone-text"><strong>Trascina qui i file</strong> oppure <span className="dropzone-link">sfoglia dal computer</span></div>
                <div className="dropzone-formats">.exe, .blockmap, .yml · selezione multipla</div>
              </div>
            </div>
            {picked.length > 0 && (
              <div className="file-list">
                <div className="file-list-head"><span>{picked.length} file selezionati</span><span className="mono">{formatSize(totalSize)}</span></div>
                {picked.map((f, i) => (
                  <div className="file-row" key={`${f.name}:${f.size}`}>
                    <span className={`badge ${extOf(f.name) === '.exe' ? 'badge-info' : 'badge-muted'}`}>{extOf(f.name)}</span>
                    <span className="file-name">{f.name}</span>
                    <span className="file-size mono">{formatSize(f.size)}</span>
                    {!uploading && <button type="button" className="file-remove" onClick={() => removeFile(i)} aria-label={`Rimuovi ${f.name}`}><Icon name="x" size={14} /></button>}
                  </div>
                ))}
              </div>
            )}
            {uploading && (
              <div className="progress" style={{ marginTop: '1.1rem' }}>
                <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>
                <span className="progress-text">{progress}%</span>
              </div>
            )}
            <button type="submit" className="btn btn-accent btn-block" style={{ marginTop: '1.25rem' }} disabled={uploading || !picked.length || !version.trim()}>
              <Icon name="upload" size={16} />{uploading ? 'Caricamento…' : 'Carica versione da testare'}
            </button>
            {message && <div className={`msg msg-${message.type}`} style={{ marginTop: '1rem' }}><Icon name={message.type === 'success' ? 'check' : 'alert'} size={16} />{message.text}</div>}
          </form>
        </div>

        <div className="card publish-status">
          <div className="card-head"><h2><Icon name="package" size={18} />Attualmente online</h2></div>
          {files.length === 0 ? (
            <div className="empty"><Icon name="package" size={30} /><span>Nessuna versione pubblicata</span><span className="empty-sub">La prima versione che pubblichi comparirà qui, pronta al download per le macchine.</span></div>
          ) : (
            <>
              <div className="status-version">
                <span className="status-version-label">Versione</span>
                <span className="status-version-num mono">{currentVersion}</span>
              </div>
              <div className="status-meta">
                <div><span className="status-meta-k">File online</span><span className="status-meta-v">{files.length}</span></div>
                <div><span className="status-meta-k">Ultimo aggiornamento</span><span className="status-meta-v">{fmt(files[0].uploaded_at)}</span></div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ---- FILE ----
  if (section === 'file') {
    return (
      <div className="card">
        <div className="card-head card-head--stacked">
          <h2><Icon name="package" size={18} />Versioni pubblicate<HelpTip text="Lo storico di tutto ciò che hai caricato. Una versione nuova nasce «Da testare» e non raggiunge nessuna macchina: va provata e poi approvata. Una sola versione per volta è «in distribuzione»: è quella che le macchine scaricano e che il tecnico installa. Le precedenti restano archiviate con i loro file, così una versione vecchia resta sempre recuperabile." /></h2>
          <span className="card-sub">
            {currentRel
              ? <>In distribuzione: <strong>v{currentRel.version}</strong> — è la versione che le macchine scaricano.</>
              : 'Nessuna versione in distribuzione.'}
            {releases.some((r) => r.status === 'da_testare') && (
              <> Da testare: <strong>{releases.filter((r) => r.status === 'da_testare').map((r) => `v${r.version}`).join(', ')}</strong> — in attesa di approvazione.</>
            )}
          </span>
          <div className="table-toolbar">
            <SearchInput id="fileSearch" placeholder="Cerca file, versione…" value={fileSearch} onChange={(e) => setFileSearch(e.target.value)} />
            {releases.some((r) => !r.is_current && r.status !== 'da_testare') && (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                style={{ marginLeft: 'auto' }}
                title="Elimina tutte le versioni archiviate (non tocca quella in distribuzione)"
                disabled={bulkDeletingVersions}
                onClick={runBulkDeleteVersions}
              >
                <Icon name="trash" size={14} />{bulkDeletingVersions ? 'Svuotamento…' : 'Svuota versioni archiviate'}
              </button>
            )}
          </div>
        </div>
        {files.length === 0 ? <div className="empty"><Icon name="package" size={30} /><span>Nessuna versione pubblicata</span></div> : versionEntries.length === 0 ? <div className="empty"><Icon name="search" size={30} /><span>Nessun risultato per i filtri applicati</span></div> : (
          <div className="acc">
            {pagedVersionEntries.map(([ver, vf]) => {
              const st = releaseStatus(ver);
              const isCurrent = !!currentRel && ver === currentRel.version;
              const relRow = releaseByVersion(ver);
              const isPending = !!relRow && relRow.status === 'da_testare';
              return (
              <div className="acc-item" key={ver}>
                <div className="acc-head">
                  <button className="acc-toggle" onClick={() => toggleVersion(ver)} type="button">
                    <span className="acc-left">
                      <span className={`acc-arrow ${openVersions[ver] ? 'open' : ''}`}><Icon name="chevronRight" size={16} /></span>
                      <span className="strong">v{ver}</span>
                      <span className={`badge ${st.cls}`}>{st.label}</span>
                      <span className="acc-count">{vf.length} file</span>
                    </span>
                    <span className="acc-date">{fmt(vf[0].uploaded_at)}</span>
                  </button>
                  {/* La versione in distribuzione non si elimina direttamente: le
                      macchine resterebbero con un aggiornamento annunciato e non
                      scaricabile. Si revoca (sposta il puntatore alla precedente),
                      poi — ora che è archiviata — diventa eliminabile come le altre. */}
                  {isPending ? (
                    <>
                      <button className="btn btn-accent btn-sm" title="Rendi questa versione l'aggiornamento distribuito alle macchine" disabled={approvingRelease} onClick={() => runApproveRelease(ver)}><Icon name="check" size={14} />Approva</button>
                      <button className="btn btn-danger btn-sm" title="Scarta la versione e i suoi file" disabled={deletingVersion} onClick={() => runDeleteVersion(ver)}><Icon name="trash" size={14} />Scarta</button>
                    </>
                  ) : !isCurrent ? (
                    <button className="btn btn-danger btn-sm" disabled={deletingVersion} onClick={() => runDeleteVersion(ver)}><Icon name="trash" size={14} />Versione</button>
                  ) : (
                    <button className="btn btn-danger btn-sm" title="Sposta la distribuzione sulla versione precedente" disabled={revokingRelease} onClick={runRevokeRelease}><Icon name="ban" size={14} />Revoca distribuzione</button>
                  )}
                </div>
                {openVersions[ver] && (
                  <div className="acc-body">
                    <div className="table-wrap">
                      <table className="table">
                        <thead><tr><th style={{ width: '30%' }}>Nome</th><th style={{ width: '14%' }}>Tipo</th><th style={{ width: '14%' }}>Dimensione</th><th style={{ width: '30%' }}>Chiave storage</th><th style={{ width: '12%' }}></th></tr></thead>
                        <tbody>
                          {vf.map((f) => (
                            <tr key={f.id}>
                              <td className="strong">{f.original_name}</td>
                              <td><span className={`badge ${f.file_type === '.exe' ? 'badge-info' : 'badge-muted'}`}>{f.file_type}</span></td>
                              <td>{formatSize(f.size)}</td>
                              <td className="mono">{f.stored_name}</td>
                              <td className="actions-cell">
                                {!isCurrent && (
                                  <button className="btn btn-danger btn-sm" disabled={deletingFile} onClick={() => runDelete(f.id)}><Icon name="trash" size={14} /></button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
        <Pagination idPrefix="filePage" total={versionEntries.length} {...filePagination} onPageChange={filePagination.setPage} onPageSizeChange={filePagination.setPageSize} itemLabel="versioni" />
      </div>
    );
  }

  // ---- EVENTI (AUDIT) ----
  if (section === 'eventi') {
    const summ = auditSummary || {};
    const kpis = [
      {
        key: 'tot', tone: 'brand', icon: 'activity', value: summ.total ?? '—', label: 'Eventi nel periodo',
        onClick: () => { setACategory(''); setAOutcome(''); setASource(''); setAuditPage(1); },
        active: !aCategory && !aOutcome && !aSource,
      },
      {
        key: 'op', tone: 'success', icon: 'users', value: summ.operators ?? '—', label: 'Azioni operatori',
        onClick: () => { setASource(aSource === 'operator' ? '' : 'operator'); setAuditPage(1); },
        active: aSource === 'operator',
      },
      {
        key: 'mac', tone: 'accent', icon: 'monitor', value: summ.machines ?? '—', label: 'Eventi macchina',
        onClick: () => { setASource(aSource === 'machine' ? '' : 'machine'); setAuditPage(1); },
        active: aSource === 'machine',
      },
      {
        key: 'err', tone: 'danger', icon: 'alert', value: summ.errors ?? '—', label: 'Errori',
        onClick: () => { setAOutcome(aOutcome === 'failure' ? '' : 'failure'); setAuditPage(1); },
        active: aOutcome === 'failure',
      },
    ];
    const totalPages = Math.max(1, Math.ceil(auditTotal / auditPageSize));
    const hasFilters = !!(aqDebounced || aCategory || aOutcome || aSource || aFrom || aTo);
    return (
      <>
        <KpiGrid items={kpis} />
        <div className="card card--fit" ref={eventCardRef}>
          <div className="card-head card-head--stacked">
            <h2><Icon name="activity" size={18} />Canale aggiornamenti<HelpTip text="Registro di audit: ogni azione sul sistema (login, gestione utenti/licenze/codici, pubblicazioni) e ogni evento delle macchine, con autore, esito e momento. Filtra per categoria, esito e origine, cerca per utente, licenza, macchina o IP, e restringi il periodo." /></h2>
            <div className="table-toolbar audit-toolbar">
              <SearchInput id="auditSearch" placeholder="Cerca utente, licenza, dispositivo, IP…" value={aq} onChange={(e) => setAq(e.target.value)} />
              <Select id="auditCategory" className={aCategory ? 'select-wrap--active' : ''} ariaLabel="Categoria" value={aCategory} onChange={(e) => { setACategory(e.target.value); setAuditPage(1); }} options={AUDIT_CATEGORY_OPTIONS} />
              <Select id="auditOutcome" className={aOutcome ? 'select-wrap--active' : ''} ariaLabel="Esito" value={aOutcome} onChange={(e) => { setAOutcome(e.target.value); setAuditPage(1); }} options={AUDIT_OUTCOME_OPTIONS} />
              <Select id="auditSource" className={aSource ? 'select-wrap--active' : ''} ariaLabel="Origine" value={aSource} onChange={(e) => { setASource(e.target.value); setAuditPage(1); }} options={AUDIT_SOURCE_OPTIONS} />
              <div className="audit-daterange">
                <DateTimePicker className="audit-date-input" ariaLabel="Da" value={aFrom} onChange={(e) => { setAFrom(e.target.value); setAuditPage(1); }} />
                <span className="audit-daterange-sep">→</span>
                <DateTimePicker className="audit-date-input" ariaLabel="A" value={aTo} onChange={(e) => { setATo(e.target.value); setAuditPage(1); }} />
              </div>
              <div className="audit-quick">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setQuickRange(0)}>Oggi</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setQuickRange(7)}>7g</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setQuickRange(30)}>30g</button>
              </div>
              <div className="audit-toolbar-actions">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { loadAudit(); loadAuditSummary(); }} disabled={auditLoading} aria-label="Aggiorna"><Icon name="refresh" size={15} />Aggiorna</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={handleExportAudit} disabled={auditExporting} aria-label="Esporta CSV"><Icon name="download" size={15} />CSV</button>
                <button type="button" className="btn btn-danger btn-sm" onClick={runPurgeAudit} disabled={purgingAudit} aria-label="Svuota registro" title="Elimina definitivamente tutti gli eventi registrati"><Icon name="trash" size={15} />{purgingAudit ? 'Svuotamento…' : 'Svuota registro'}</button>
              </div>
            </div>
          </div>
          {auditItems.length === 0 ? (
            <div className="empty"><Icon name="activity" size={30} /><span>{hasFilters ? 'Nessun evento per i filtri applicati' : 'Nessun evento registrato'}</span></div>
          ) : (
              <div className="table-wrap" ref={eventWrapRef}>
                <table className="table audit-table">
                  <thead ref={eventTheadRef}>
                    <tr>
                      <th style={{ width: '15%' }}>Quando</th>
                      <th style={{ width: '12%' }}>Categoria</th>
                      <th style={{ width: '18%' }}>Azione</th>
                      <th style={{ width: '9%' }}>Esito</th>
                      <th style={{ width: '15%' }}>Attore</th>
                      <th style={{ width: '15%' }}>Target</th>
                      <th style={{ width: '10%' }}>IP</th>
                      <th style={{ width: '6%' }} aria-label="Dettaglio"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditItems.map((ev) => {
                      const cat = AUDIT_CATEGORIES[ev.category] || { label: ev.category, tone: 'brand' };
                      const out = AUDIT_OUTCOMES[ev.outcome] || { label: ev.outcome, cls: 'badge-muted' };
                      const actor = auditActor(ev);
                      const detail = auditShortDetail(ev);
                      return (
                        <tr key={ev.evt_id} className="audit-row" onClick={() => setAuditDetail(ev)}>
                          <td className="audit-when">{fmt(ev.occurred_at)}</td>
                          <td><span className={`badge audit-cat tone-${cat.tone}`}>{cat.label}</span></td>
                          <td className="strong audit-action">{auditActionLabel(ev.action)}{detail ? <span className="audit-detail"> · {detail}</span> : null}</td>
                          <td><span className={`badge ${out.cls}`}>{out.label}</span></td>
                          <td className="audit-actor"><span className="audit-actor-main">{actor.main}</span>{actor.sub ? <span className="audit-actor-sub">{actor.sub}</span> : null}</td>
                          <td className="audit-target">
                            <span className="audit-target-main">{ev.target_label || ev.target_type || '—'}</span>
                            {ev.device_code ? <span className="audit-target-sub"><Icon name="monitor" size={11} />{ev.device_code}</span> : null}
                          </td>
                          <td className="mono audit-ip">{ev.ip || '—'}</td>
                          <td className="audit-open"><Icon name="chevronRight" size={15} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
          )}
          <Pagination idPrefix="auditPage" page={auditPage} pageSize={auditPageSize} totalPages={totalPages} total={auditTotal} onPageChange={setAuditPage} onPageSizeChange={() => {}} showSizeSelector={false} itemLabel="eventi" />
        </div>
        {auditDetail && (
          <Drawer title="Dettaglio evento" icon="activity" onClose={() => setAuditDetail(null)}>
            {(() => {
              const ev = auditDetail;
              const cat = AUDIT_CATEGORIES[ev.category] || { label: ev.category, tone: 'brand' };
              const out = AUDIT_OUTCOMES[ev.outcome] || { label: ev.outcome, cls: 'badge-muted' };
              const actor = auditActor(ev);
              const meta = (ev.metadata && typeof ev.metadata === 'object') ? ev.metadata : {};
              const metaEntries = Object.entries(meta).filter(([, v]) => v !== null && v !== undefined && v !== '');
              return (
                <div className="audit-detail-panel">
                  <div className="audit-detail-title">
                    <span className="strong">{auditActionLabel(ev.action)}</span>
                    <span className={`badge ${out.cls}`}>{out.label}</span>
                  </div>
                  <div className="audit-detail-block">
                    <span className="audit-detail-k">Chi</span>
                    <span className="audit-detail-v">{actor.main}{actor.sub ? ` (${actor.sub})` : ''}</span>
                  </div>
                  <div className="audit-detail-block">
                    <span className="audit-detail-k">Cosa</span>
                    <span className="audit-detail-v"><span className={`badge audit-cat tone-${cat.tone}`}>{cat.label}</span> {auditActionLabel(ev.action)}</span>
                  </div>
                  {(ev.target_label || ev.target_type) && (
                    <div className="audit-detail-block">
                      <span className="audit-detail-k">Target</span>
                      <span className="audit-detail-v">{ev.target_label || '—'}{ev.target_type ? <span className="audit-detail-sub"> · {ev.target_type}</span> : null}</span>
                    </div>
                  )}
                  {ev.device_code && (
                    <div className="audit-detail-block">
                      <span className="audit-detail-k">Dispositivo</span>
                      <span className="audit-detail-v mono">{ev.device_code}</span>
                    </div>
                  )}
                  <div className="audit-detail-block">
                    <span className="audit-detail-k">Dove</span>
                    <span className="audit-detail-v mono">{ev.ip || '—'}</span>
                  </div>
                  {ev.user_agent && (
                    <div className="audit-detail-block">
                      <span className="audit-detail-k">Client</span>
                      <span className="audit-detail-v audit-detail-ua">{ev.user_agent}</span>
                    </div>
                  )}
                  <div className="audit-detail-block">
                    <span className="audit-detail-k">Quando</span>
                    <span className="audit-detail-v">{fmt(ev.occurred_at)}</span>
                  </div>
                  {metaEntries.length > 0 && (
                    <div className="audit-detail-meta">
                      <span className="audit-detail-k">Contesto</span>
                      <div className="audit-detail-kv">
                        {metaEntries.map(([k, v]) => (
                          <div className="audit-kv-row" key={k}>
                            <span className="audit-kv-key">{k}</span>
                            <span className="audit-kv-val mono">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </Drawer>
        )}
      </>
    );
  }

  return null;
}

export default DashboardSviluppatore;
